import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import UnauthorizedAccess from "components/RequirePermission/UnauthorizedAccess";
import { useCopilotActivityCapabilities, useCopilotUsageCapabilities } from "components/RequirePermission/useCapabilities";
import UserProfileButton from "components/shared/UserProfileButton/UserProfileButton";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetCopilotUserActivityQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useGetActiveOrganizationsQuery, useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useGetCostCenterUsersQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";
import { useDebounce } from "utils/useDebounce";
import BudgetUsageCell, { SCOPE_LABELS } from "pages/admin/components/CopilotDashboard/BudgetUsageCell";
import UserDetailModal from "pages/admin/components/CopilotDashboard/UserDetailModal";
import AvatarRandomColor from "pages/OrgHistory/components/ReusableDataGrid/AvatarRandomColor";
import { formatActivityParts } from "utils/formatLocalDateTime";
import { generateRandomColor } from "utils/generateRandomColor";

const ACCENT = "#26890d";
const PER_PAGE = 10;

const STATUS_TABS = [
  {
    key: "active",
    label: "Active",
    help: "Last Copilot activity falls inside the date range shown.",
  },
  {
    key: "inactive",
    label: "Inactive",
    help: "Last activity was before the date range, or the user has never used Copilot.",
  },
];

// Directory names come as "Last, First" (e.g. "Cherukuru, Vamsi"); anything
// else is treated as "First ... Last". Falls back to the username/email so the
// avatar is never blank.
const getAvatarName = (user) => {
  const fullName = user.full_name?.trim();
  if (fullName?.includes(",")) {
    const [last, first] = fullName.split(",").map((part) => part.trim());
    return { firstName: first, lastName: last };
  }
  if (fullName) {
    const parts = fullName.split(/\s+/);
    return { firstName: parts[0], lastName: parts.length > 1 ? parts[parts.length - 1] : "" };
  }
  const fallback = user.user_name ?? user.email ?? "?";
  return { firstName: fallback, lastName: "" };
};

// "2026-10-01" -> "1 Oct 2026" (parsed as a plain calendar date, so no timezone shift).
const formatDay = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// One line, cut off with "…" past maxWidth; the full text is in a tooltip.
const EllipsisText = ({ text, tooltip, maxWidth = 260, sx }) => (
  <Tooltip title={tooltip ?? text} placement="top" arrow enterDelay={400}>
    <Typography noWrap sx={{ maxWidth, ...sx }}>
      {text}
    </Typography>
  </Tooltip>
);

// "Filter By" fields — one active at a time, like the AIC Budget Requests page.
// Switching the field clears the value.
const FILTER_FIELDS = [
  { value: "email", label: "Email" },
  { value: "cost_center", label: "Cost Center" },
  { value: "budget_type", label: "Budget Type" },
  { value: "consumed", label: "Consumed ($)" },
];
const DEFAULT_SORT = { by: "last_activity_at", order: "desc" };

// Header label with MUI's sort arrow; the arrow is faint until the column is the active sort.
const SortHeader = ({ label, field, sortBy, sortOrder, onSort }) => (
  <TableSortLabel
    active={sortBy === field}
    direction={sortBy === field ? sortOrder : "asc"}
    onClick={() => onSort(field)}
    sx={{ "& .MuiTableSortLabel-icon": { opacity: sortBy === field ? 1 : 0.35 } }}
  >
    {label}
  </TableSortLabel>
);

const filterInputSx = {
  width: 240,
  "& .MuiOutlinedInput-root": {
    height: 34,
    fontSize: "13px",
    background: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: "#bdbdbd" },
    "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: 1 },
  },
};

const headCellSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};
const bodyCellSx = { fontSize: "0.85rem", color: "#1a1a1a", py: 1.2, borderBottom: "1px solid #f0f0f0" };

// Active/inactive Copilot users for the current billing cycle. Backed by
// GET /users/copilot/users/activity, which returns one bucket per call. No dates
// are sent: the API resolves "1st of this month → today" itself and echoes the
// window back, which is shown in the subtitle.
const CopilotUserActivity = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { canViewActivity } = useCopilotActivityCapabilities(orgId);
  const { data: orgData } = useGetOrgByIdQuery(orgId);

  // The view lives in the URL (?status=&filter=&q=&sort=&order=&page=) so a link
  // restores the same tab, filter, sort and page. Defaults are left out of the
  // URL, and every change uses replace so Back doesn't step through keystrokes.
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") === "inactive" ? "inactive" : "active";
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  const filterField = FILTER_FIELDS.some((f) => f.value === searchParams.get("filter")) ? searchParams.get("filter") : "email";
  const filterValue = searchParams.get("q") ?? "";
  // Consumed ($) range: min/max live in the URL as plain numbers.
  const minConsumed = searchParams.get("min") ?? "";
  const maxConsumed = searchParams.get("max") ?? "";
  const sortBy = searchParams.get("sort") === "email" ? "email" : DEFAULT_SORT.by;
  const sortOrder = searchParams.get("order") === "asc" ? "asc" : searchParams.get("order") === "desc" ? "desc" : sortBy === "email" ? "asc" : DEFAULT_SORT.order;

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace: true });
  };

  // Email is typed freely, so it's debounced before it reaches the URL (and the API).
  const [emailInput, setEmailInput] = useState(filterField === "email" ? filterValue : "");
  const debouncedEmail = useDebounce(emailInput, 400).trim();
  useEffect(() => {
    if (filterField === "email" && debouncedEmail !== filterValue) updateParams({ q: debouncedEmail, page: null });
    // Only the debounced text should trigger this — not the URL values it compares against.
  }, [debouncedEmail]);

  // The consumed range is typed too, so it's debounced into the URL the same way.
  const [minInput, setMinInput] = useState(minConsumed);
  const [maxInput, setMaxInput] = useState(maxConsumed);
  const debouncedMin = useDebounce(minInput, 400).trim();
  const debouncedMax = useDebounce(maxInput, 400).trim();
  useEffect(() => {
    if (filterField === "consumed" && (debouncedMin !== minConsumed || debouncedMax !== maxConsumed)) {
      updateParams({ min: debouncedMin, max: debouncedMax, page: null });
    }
  }, [debouncedMin, debouncedMax]);

  // Cost center ids -> names (same list the Joined tab uses; cached).
  const { data: platformData } = useGetPlatformsQuery();
  const { data: costCenterListData } = useGetCostCenterUsersQuery(
    { platformId: platformData?._id },
    { skip: !platformData?._id }
  );
  const costCenterNames = Object.fromEntries((costCenterListData?.cost_centers ?? []).map((cc) => [cc._id, cc.name]));

  // Both tabs' first page (10 users) load together, so each tab's count is
  // there on arrival and switching tabs is instant. The other tab always asks
  // for page 1 — exactly the request it'll need if you open it — so nothing is
  // fetched twice. Results are cached (see copilotQueries.js), so returning to a
  // tab or page you've already seen sends nothing.
  // currentData (not data) is only ever the result for exactly these args, so a
  // tab/page switch never shows the previous tab's rows while a request is in flight.
  const argsFor = (bucket) => ({
    orgId,
    status: bucket,
    search: filterField === "email" && filterValue ? filterValue : undefined,
    costCenterId: filterField === "cost_center" && filterValue ? filterValue : undefined,
    budgetScope: filterField === "budget_type" && filterValue ? filterValue : undefined,
    minConsumedAmount: filterField === "consumed" && minConsumed !== "" ? minConsumed : undefined,
    maxConsumedAmount: filterField === "consumed" && maxConsumed !== "" ? maxConsumed : undefined,
    // Only sent when it differs from the API's own default (last activity, newest first).
    ...(sortBy !== DEFAULT_SORT.by || sortOrder !== DEFAULT_SORT.order ? { sortBy, sortOrder } : {}),
    page: bucket === status ? page : 1,
    perPage: PER_PAGE,
  });
  const activeQuery = useGetCopilotUserActivityQuery(argsFor("active"), { skip: !canViewActivity });
  const inactiveQuery = useGetCopilotUserActivityQuery(argsFor("inactive"), { skip: !canViewActivity });
  const { currentData: data, isLoading, isFetching, error, refetch } = status === "active" ? activeQuery : inactiveQuery;
  const counts = { active: activeQuery.currentData?.total, inactive: inactiveQuery.currentData?.total };

  // One icon per row opens the merged user modal: the Copilot usage for this billing cycle plus the
  // user's profile and organizations, which load when it opens.
  const {
    canViewUsage: canViewUsageBase,
    canViewUsageById,
    canViewModelUsage,
    canViewUsageByCostCenter,
    canViewUsageByCostCenterId,
  } = useCopilotUsageCapabilities(orgId);
  const canViewCopilotUsage =
    canViewUsageBase && canViewUsageById && canViewModelUsage && canViewUsageByCostCenter && canViewUsageByCostCenterId;
  const { data: allOrgs } = useGetActiveOrganizationsQuery();
  const orgIdToName = Object.fromEntries((allOrgs ?? []).map((org) => [org._id, org.name]));
  const [triggerGetUser] = useLazyGetUserQuery();
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");

  const handleOpenUser = async (row) => {
    setSelectedUser(row);
    setProfileUser(null);
    setProfileError("");
    setIsProfileLoading(true);
    try {
      const result = await triggerGetUser({ email: row.email, platformId: platformData?._id });
      const users = Array.isArray(result?.data) ? result.data : [];
      setProfileUser(users.find((u) => u._id === row.user_id) || users[0] || null);
      if (!users.length) setProfileError("Could not find user details for this user.");
    } catch (err) {
      setProfileError("Failed to load user details. Please try again.");
    } finally {
      setIsProfileLoading(false);
    }
  };

  if (!canViewActivity) {
    return (
      <UnauthorizedAccess
        message="You do not have permission to view Copilot user activity."
        primaryLabel="Back to Copilot dashboard"
        primaryTo={`/admin/${orgId}/copilot-dashboard`}
      />
    );
  }

  // The API resolves the billing-cycle window itself and echoes it back.
  const cycle = activeQuery.currentData ?? inactiveQuery.currentData;
  const windowLabel = cycle?.from_date && cycle?.to_date ? `${formatDay(cycle.from_date)} – ${formatDay(cycle.to_date)}` : null;

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const nPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const setPage = (next) => updateParams({ page: next === 1 ? null : next });
  const changeStatus = (_, next) => updateParams({ status: next === "active" ? null : next, page: null });
  const changeFilterField = (next) => {
    setEmailInput("");
    setMinInput("");
    setMaxInput("");
    updateParams({ filter: next === "email" ? null : next, q: null, min: null, max: null, page: null });
  };
  const changeFilterValue = (value) => updateParams({ q: value, page: null });
  // Email asks for A→Z first, last activity for newest first; clicking the active column flips it.
  const handleSort = (field) => {
    const order = sortBy === field ? (sortOrder === "asc" ? "desc" : "asc") : field === "email" ? "asc" : "desc";
    updateParams({ sort: field === DEFAULT_SORT.by ? null : field, order: field === DEFAULT_SORT.by && order === DEFAULT_SORT.order ? null : order, page: null });
  };
  const hasFilter = filterField === "consumed" ? minConsumed !== "" || maxConsumed !== "" : Boolean(filterValue);
  const costCenters = costCenterListData?.cost_centers ?? [];

  const errorStatus = error?.status;
  const errorMessage =
    errorStatus === 403
      ? "You do not have permission to view Copilot user activity."
      : errorStatus === 400
        ? error?.data?.error_message ?? "The request was invalid."
        : "Failed to load Copilot user activity.";

  return (
    <Box
      sx={{
        flex: "1",
        marginTop: { xs: "1.5rem", md: "2rem" },
        marginBottom: { xs: "1.5rem", md: "2rem" },
        marginLeft: { xs: "1rem", md: "4rem" },
        marginRight: { xs: "1rem", md: "4rem" },
        backgroundColor: "#F3F3F3",
        borderRadius: "12px",
        padding: { xs: "16px", md: "24px" },
        // Fit the viewport on desktop (navbar + the 2rem top/bottom margins) so the
        // table scrolls inside and the pagination footer is always on screen.
        display: "flex",
        flexDirection: "column",
        height: { md: "calc(100vh - 7.5rem)" },
        minHeight: { md: 520 },
        minWidth: 0,
      }}
    >
      {/* One compact header: back arrow + title + org on the left, the billing-cycle
          window on the right, with the subtitle tucked directly underneath. */}
      <Box sx={{ mb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Tooltip title="Back to Copilot Dashboard" placement="top" arrow>
              <IconButton
                size="small"
                aria-label="Back to Copilot Dashboard"
                onClick={() => navigate(`/admin/${orgId}/copilot-dashboard`)}
                sx={{ color: "#555", ml: -0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
              Copilot Users
            </Typography>
            {orgData?.name && (
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#43B02A", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {orgData.name}
              </Typography>
            )}
          </Box>

          {windowLabel && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.7,
                px: 1.4,
                py: 0.5,
                backgroundColor: "rgba(38,137,13,0.07)",
                border: "1px solid rgba(38,137,13,0.22)",
                borderRadius: "20px",
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 14, color: ACCENT }} />
              <Typography sx={{ fontSize: "0.82rem", color: ACCENT, fontWeight: 700 }}>
                Billing cycle: {windowLabel}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: "0.8rem", color: "#888", mt: 0.2, ml: 4 }}>
          Active and inactive Copilot users this billing cycle
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e8e8e8",
          backgroundColor: "#fff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, borderBottom: "1px solid #f0f0f0", pr: 2, flexShrink: 0 }}>
        <Tabs
          value={status}
          onChange={changeStatus}
          sx={{ px: 2, minHeight: 44 }}
          TabIndicatorProps={{ style: { backgroundColor: ACCENT } }}
        >
          {STATUS_TABS.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.9rem", minHeight: 44, "&.Mui-selected": { color: ACCENT } }}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  {counts[tab.key] == null ? tab.label : `${tab.label} (${counts[tab.key].toLocaleString("en-AU")})`}
                  <Tooltip title={tab.help} placement="top" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: "#aaa" }} />
                  </Tooltip>
                </Box>
              }
            />
          ))}
        </Tabs>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>Filter By</Typography>
            <Select
              size="small"
              value={filterField}
              onChange={(e) => changeFilterField(e.target.value)}
              inputProps={{ "aria-label": "Filter by" }}
              SelectDisplayProps={{ "aria-label": "Filter by" }}
              sx={{ minWidth: 140, height: 34, fontSize: "13px", background: "#fff", borderRadius: "8px", "& fieldset": { borderColor: "#e0e0e0" } }}
            >
              {FILTER_FIELDS.map((f) => (
                <MenuItem key={f.value} value={f.value} sx={{ fontSize: "13px" }}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>

            {filterField === "email" ? (
              <TextField
                size="small"
                placeholder="Search by email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                  endAdornment: emailInput && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => setEmailInput("")} sx={{ p: "2px" }}>
                        <CloseOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={filterInputSx}
              />
            ) : filterField === "consumed" ? (
              <>
                {[
                  ["Min", minInput, setMinInput],
                  ["Max", maxInput, setMaxInput],
                ].map(([label, value, setValue]) => (
                  <TextField
                    key={label}
                    size="small"
                    type="number"
                    placeholder={`${label} $`}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    inputProps={{ min: 0, step: "any", "aria-label": `${label} consumed amount` }}
                    sx={{ ...filterInputSx, width: 130 }}
                  />
                ))}
              </>
            ) : filterField === "cost_center" ? (
              <Autocomplete
                size="small"
                options={costCenters}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={costCenters.find((cc) => cc._id === filterValue) ?? null}
                onChange={(_, option) => changeFilterValue(option?._id ?? "")}
                componentsProps={{ popper: { style: { width: "fit-content", minWidth: 260 } } }}
                renderInput={(params) => <TextField {...params} placeholder="Select cost center" />}
                sx={{ ...filterInputSx, width: 260 }}
              />
            ) : (
              <Select
                size="small"
                displayEmpty
                value={filterValue}
                onChange={(e) => changeFilterValue(e.target.value)}
                inputProps={{ "aria-label": "Budget type" }}
                SelectDisplayProps={{ "aria-label": "Budget type" }}
                renderValue={(value) => (value ? SCOPE_LABELS[value] : <span style={{ color: "#9ca3af" }}>Select budget type</span>)}
                sx={{ ...filterInputSx, minWidth: 220 }}
              >
                <MenuItem value="" sx={{ fontSize: "13px", color: "#888" }}>
                  All budget types
                </MenuItem>
                {Object.entries(SCOPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: "13px" }}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Box>

        {error ? (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={errorStatus !== 403 && <Button size="small" onClick={refetch}>Retry</Button>}
          >
            {errorMessage}
          </Alert>
        ) : isLoading || (isFetching && !data) ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 240 }}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
            <Typography sx={{ fontSize: "0.85rem", color: "#999", fontStyle: "italic" }}>
              {hasFilter ? `No ${status} users match this filter.` : `No ${status} users for this period.`}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ opacity: isFetching ? 0.5 : 1, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 56 }}>#</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 80 }} align="center">Profile</TableCell>
                  <TableCell sx={headCellSx}>User</TableCell>
                  <TableCell sx={headCellSx} sortDirection={sortBy === "email" ? sortOrder : false}>
                    <SortHeader label="Email" field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  </TableCell>
                  <TableCell sx={headCellSx}>Cost center</TableCell>
                  <TableCell sx={headCellSx}>Budget</TableCell>
                  <TableCell sx={headCellSx} sortDirection={sortBy === "last_activity_at" ? sortOrder : false}>
                    <SortHeader label="Last activity" field="last_activity_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, idx) => (
                  <TableRow key={user.user_id} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ ...bodyCellSx, color: "#999" }}>{(page - 1) * PER_PAGE + idx + 1}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <UserProfileButton
                        userLabel={user.email ?? "this user"}
                        onClick={() => handleOpenUser(user)}
                        // Everyone here is a Copilot user for this org; an inactive user with no budget
                        // record at all has no license to show.
                        licensedOrgNames={status === "active" || user.budget ? [orgData?.name ?? "this organization"] : []}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <AvatarRandomColor {...getAvatarName(user)} color={generateRandomColor(user.user_id)} />
                        <Box>
                          <EllipsisText
                            text={user.full_name ?? user.user_name ?? "—"}
                            sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}
                          />
                          {user.full_name && user.user_name && (
                            <EllipsisText text={`@${user.user_name}`} sx={{ fontSize: "0.72rem", color: "#999" }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <EllipsisText text={user.email ?? "—"} sx={{ fontSize: "0.85rem", color: "#1a1a1a" }} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {user.cost_center_id
                        ? (costCenterNames[user.cost_center_id] ? (
                            <EllipsisText
                              text={costCenterNames[user.cost_center_id]}
                              tooltip={`Cost Center: ${costCenterNames[user.cost_center_id]}`}
                              sx={{ fontSize: "0.85rem", color: "#1a1a1a" }}
                            />
                          ) : (
                            <Typography component="span" sx={{ fontSize: "0.8rem", color: "#999", fontStyle: "italic" }}>
                              Unknown cost center
                            </Typography>
                          ))
                        : "—"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <BudgetUsageCell budget={user.budget} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {user.last_activity_at ? (
                        (() => {
                          const { date, time } = formatActivityParts(user.last_activity_at);
                          return (
                            <>
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>{date}</Typography>
                              <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>{time}</Typography>
                            </>
                          );
                        })()
                      ) : (
                        <Typography component="span" sx={{ fontSize: "0.82rem", color: "#999", fontStyle: "italic" }}>
                          No activity recorded
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!error && !isLoading && total > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              px: 3,
              py: 1.5,
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} of {total.toLocaleString("en-AU")} {status} users
            </Typography>
            {nPages > 1 && <Pagination nPages={nPages} currentPage={page} setCurrentPage={setPage} />}
          </Box>
        )}
      </Paper>

      <UserDetailModal
        open={Boolean(selectedUser)}
        onClose={() => {
          setSelectedUser(null);
          setProfileUser(null);
          setProfileError("");
        }}
        user={selectedUser}
        orgId={orgId}
        platformId={platformData?._id}
        fromDate={canViewCopilotUsage ? cycle?.from_date : undefined}
        toDate={canViewCopilotUsage ? cycle?.to_date : undefined}
        canViewUsage={canViewCopilotUsage}
        footerNote={canViewCopilotUsage && cycle?.from_date ? "Showing the current billing cycle's usage. Visit the Copilot Dashboard to view other date ranges." : undefined}
        profile={{
          user: profileUser,
          orgIdToName,
          costCenterById: costCenterNames,
          isLoading: isProfileLoading,
          error: profileError,
        }}
      />
    </Box>
  );
};

export default CopilotUserActivity;
